import type {PaymentReferencesResource} from '@api/models/PaymentReferencesResource';
import type {PaymentReferencesUpdateRequest} from '@api/models/PaymentReferencesUpdateRequest';

export interface PaymentReferencesForm {
    iban?: string;
    bic?: string;
    wireReference?: string;
    beneficiary?: string;
}

/**
 * Builds the payment references request to include in an invoice update.
 * Sends the form values whenever all required fields (iban, bic, wireReference) are filled,
 * regardless of whether they differ from the original invoice data.
 * This ensures the amended invoice (B) always receives the payment references
 * shown in the update form, even when the original invoice (A) had empty PG columns
 * and the values came from the WS enricher.
 */
export function buildPaymentReferencesRequest(refs: PaymentReferencesForm): PaymentReferencesUpdateRequest | undefined {
    if (!refs.iban || !refs.bic || !refs.wireReference) {
        return undefined;
    }
    return {
        iban: refs.iban,
        bic: refs.bic,
        wireReference: refs.wireReference,
        beneficiary: refs.beneficiary,
    };
}

/**
 * Returns true if the form payment references differ from the original invoice data.
 * Used to determine if the "save" button should be enabled.
 * Note: this comparison is against invoice.paymentReferences from the API response.
 * When the invoice has empty PG columns and the API returns null paymentReferences,
 * any filled form value will be considered "edited".
 */
export function isPaymentReferencesEdited(formRefs: PaymentReferencesForm, originalRefs: PaymentReferencesResource | null | undefined): boolean {
    return (
        formRefs.iban !== originalRefs?.iban ||
        formRefs.bic !== originalRefs?.bic ||
        formRefs.wireReference !== originalRefs?.wireReference ||
        formRefs.beneficiary !== originalRefs?.beneficiary
    );
}
